import { t } from 'i18next';
import { useCallback } from 'react';

import { RutterConnection } from '~hooks/useRutter/models';
import useRutter from '~hooks/useRutter/useRutter';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import usePopupSession from '~utils/hooks/usePopupSession';

export default function useCreateRutterConnection({
  onLinked,
}: {
  onLinked: () => void;
}) {
  const { toasterStore, rutterIntegrationStore } = useStores();
  const { createConnection, enqueueImport } = useRutter();
  const { openBlankPopup, watchPopupClose } = usePopupSession();

  const watchRutterLinkPopup = useCallback(
    (
      popup: Window,
      rutterConnection: RutterConnection,
      successMessage: string,
      onClosed?: () => void,
    ) => {
      const userClosedPopupBeforeLink = popup.closed;

      if (userClosedPopupBeforeLink || !rutterConnection.linkUrl) {
        return false;
      }

      popup.location.href = rutterConnection.linkUrl;

      watchPopupClose(popup, async () => {
        onClosed?.();
        try {
          await enqueueImport(rutterConnection.id);
          toasterStore.push(alert(successMessage, AlertTypes.success));
        } catch {
          // enqueueImport already surfaced the failure toast
        } finally {
          onLinked();
        }
      });

      return true;
    },
    [enqueueImport, onLinked, watchPopupClose],
  );

  const openBlankRutterPopup = useCallback(
    () => openBlankPopup(t('error_messages.rutter.failed_to_open_link')),
    [openBlankPopup],
  );

  const createRutterConnection = useCallback(async () => {
    const popup = openBlankRutterPopup();
    if (!popup) {
      return false;
    }

    rutterIntegrationStore.createConnectionStart();

    const rutterConnection = await createConnection();
    if (!rutterConnection) {
      popup.close();
      rutterIntegrationStore.createConnectionEnd();
      return false;
    }

    const isWatchingPopup = watchRutterLinkPopup(
      popup,
      rutterConnection,
      t('administration.integration.rutter.grid.connection_created'),
      () => {
        rutterIntegrationStore.createConnectionEnd(rutterConnection);
      },
    );

    if (!isWatchingPopup) {
      // The connection persisted server-side, so keep its pending row for a relink.
      popup.close();
      rutterIntegrationStore.createConnectionEnd(rutterConnection);
    }

    return isWatchingPopup;
  }, [
    createConnection,
    openBlankRutterPopup,
    rutterIntegrationStore,
    watchRutterLinkPopup,
  ]);

  const relinkRutterConnection = useCallback(
    (rutterConnection: RutterConnection) => {
      const popup = openBlankRutterPopup();
      if (!popup) {
        return;
      }

      if (
        !watchRutterLinkPopup(
          popup,
          rutterConnection,
          t('administration.integration.rutter.grid.connection_linked'),
        )
      ) {
        popup.close();
      }
    },
    [openBlankRutterPopup, watchRutterLinkPopup],
  );

  return { createRutterConnection, relinkRutterConnection };
}
